import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import DynamicTable from "../../../../components/learning-development/common/DynamicTable";
import api from "../../../../Axios.js";
import CommonFilter from "../../../../components/common/CommonFilter.jsx";
import useLoader from "../../../../hooks/useLoader.js";
import ExpandableText from "../../../../components/common/ExpandableText.jsx";
import { Tooltip } from "@mui/material";
import UploadIcon from '@mui/icons-material/Upload';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import useModal from "../../../../hooks/useModal.js";
import CertificateUploadModal from "./CertificateUploadModal.jsx";
import UserInfoCell from "../../../../components/common/UserInfoCell.jsx";

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const AssignedEmployeeList = ({ assignedId, endDate, documentPath }) => {

    const { hideLoader, showLoader } = useLoader();
    const { showModal } = useModal();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage] = useState(20);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({ search: "" });
    const [modalType, setModalType] = useState(null);

    const filterConfig = useMemo(
        () => [
            {
                name: "search",
                type: "text",
                label: "Employee Name/Code",
                grid: 4,
            },
        ],
        []
    );

    const closeModal = () => {
        setModalType(null);
        setSelectedRequest(null);
    };

    const fetchAssignments = useCallback(
        async (page = currentPage, customFilters = filters) => {
            try {
                showLoader();

                const res = await api.post(
                    "/lnd/get-assigned-user-list-by-assignment-id",
                    {
                        assigned_id: assignedId,
                        page,
                        limit: itemsPerPage,
                        searchData: {
                            ...customFilters,
                            search: customFilters.search?.trim() || ""
                        }
                    }
                );

                const responseData = res.data?.data;

                setAssignments(responseData?.data || []);

                const pagination = responseData?.pagination || {};
                setTotalPages(pagination.totalPages || 1);
                setTotalItems(pagination.totalRecords || 0);
            } catch (err) {
                toast.error("Failed to fetch assigned users");
            } finally {
                hideLoader();
            }
        },
        [assignedId, currentPage, itemsPerPage, filters]
    );

    const openModal = async (type, row) => {
        setSelectedRequest(row);
        setModalType(type);
    };

    const handleCertficateOpen = (key, title, certificate_img = "") => {
        let body;

        if (key === "certificate") {
            body = (
                <div className="text-center">
                    {certificate_img ? (
                        <img
                            src={BASE_URL + documentPath + certificate_img}
                            alt="Certificate"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "500px",
                                borderRadius: "8px"
                            }}
                        />
                    ) : (
                        <p>No certificate available</p>
                    )}
                </div>
            );
        }

        showModal(title, body);
    };

    const handleExport = async (filters) => {
        try {
            const res = await api.post(
                "/lnd/export-assigned-program-list",
                { searchData: filters },
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }));
            const link = document.createElement("a");
            link.href = url;
            link.download = "assigned_program_export.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Export failed");
        }
    };

    useEffect(() => {
        if (assignedId) {
            fetchAssignments(currentPage, filters);
        }
    }, [currentPage, assignedId]);

    const columns = useMemo(
        () => [
            {
                label: "Employee Name (Code)",
                render: (row) =>
                    <div className="text-decoration-none">
                        {UserInfoCell({ name: `${row.user?._FirstName} ${row?.user?._MiddleName} ${row.user?._LastName}`, employeeId: row.user?._UserEmployeeID, token: row?.user?._UserID })}
                    </div>
            },
            {
                label: "Rating",
                render: (row) => row.rating ?? "--",
            },
            {
                label: "Feedback",
                render: (row) => <ExpandableText text={row.feedback ?? "--"} label="Feedback Description" />,
            },
            {
                label: "Progress (%)",
                render: (row) => row.progress_percentage ?? 0,
            },
            {
                label: "Status",
                render: (row) => {

                    return row.status === "completed"
                        ? "Completed"
                        : row.status === "in_progress"
                            ? "In Progress"
                            : "Not Started";
                },
            },
            {
                label: "Upload Certificate",
                render: (row) => {
                    if (row["status"] === "completed") {
                        return (
                            <div>
                                <Tooltip title="Upload" placement="top" arrow>
                                    <UploadIcon
                                        className="cursor-pointer"
                                        sx={{ color: "black" }}
                                        onClick={() => openModal("certificate", row)}
                                    />
                                </Tooltip>

                                {row?.certificate_img && <Tooltip title="View" placement="top" arrow>
                                    <RemoveRedEyeIcon
                                        className="cursor-pointer"
                                        sx={{ color: "black", marginLeft: "8px" }}
                                        onClick={() => handleCertficateOpen("certificate", "Certificate", row?.certificate_img)}
                                    />
                                </Tooltip>}
                            </div>
                        );
                    }
                    return "--";
                }
            },
        ],
        []
    );

    // ── Filter Submit ──
    const handleFilterSubmit = (data) => {
        setFilters(data);
        setCurrentPage(1);
        fetchAssignments(1, data);
    };

    return (
        <div className="py-4">
            {/* ── Filters ── */}
            <div className="mb-4">
                <CommonFilter
                    config={filterConfig}
                    defaultValues={{ search: "" }}
                    onSubmit={handleFilterSubmit}
                    // onExport={handleExport}
                />
            </div>

            {/* ── Table ── */}
            <DynamicTable
                columns={columns}
                data={assignments}
                loading={loading}
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                rowKey="id"
            />

            {
                modalType === "certificate" && (
                    <CertificateUploadModal
                        isOpen={true}
                        onClose={closeModal}
                        certificateUrl={selectedRequest?.certificate_img || null}
                        fetchAssignments={fetchAssignments}
                        programId={selectedRequest?.program_id}
                    />
                )
            }
        </div>
    );
};

export default AssignedEmployeeList;